import type { Chart } from 'chart.js';
import { useCallback, useState } from 'react';
import { HtmlLegend } from '@/components/UI/Charts/HtmlLegend';
import { createHtmlTooltipPlugin } from '@/components/UI/Charts/PieChart/htmlTooltipPlugin';
import { NumberAbbreviation } from '@/components/UI/NumberFormatDisplay/NumberAbbreviation';
import { useDashboardData } from '@/hooks/ReactQueryHooks/useDashboardQuery';
import type { DashboardType } from '@/types/Analytics.ts';
import { transformProjectTypeChartDataSets } from '@/utils';
import Card from '../../UI/Card/index.tsx';
import PieChart from '../../UI/Charts/PieChart';
import { Widget } from '../constants';
import WidgetLayout from '../WidgetLayout';

export interface ProjectTypeProps {
  projectType: string;
  _count: number;
  units: number;
}

export default function ProjectTypeWidget({ dashboard }: { dashboard: DashboardType }) {
  const fetchProjectTypeWidgetData = useDashboardData({
    dashboard,
    widget: Widget.PROJECTS_BY_PROJECT_TYPE,
  });

  const [pieChart, setPieChart] = useState<Chart<'pie'> | null>(null);
  const [htmlTooltipPlugin] = useState(() => createHtmlTooltipPlugin());

  const handlePieChart = useCallback((node: Chart<'pie'>) => {
    setPieChart(node);
  }, []);

  const projectTypeWidgetData = fetchProjectTypeWidgetData?.data?.data;
  const projectTypeLabels = projectTypeWidgetData?.map((data: ProjectTypeProps) => data?.projectType);

  const projectsByProjectType = projectTypeWidgetData?.map((data: ProjectTypeProps) => ({
    type: data?.projectType,
    count: data?._count,
  }));

  const totalProjectType = projectTypeWidgetData?.reduce((acc: number, data: ProjectTypeProps) => acc + data?._count, 0);

  return (
    <>
      {projectTypeWidgetData && (
        <WidgetLayout dashboard={dashboard} widget={Widget.PROJECTS_BY_PROJECT_TYPE}>
          <Card title='Projects By Development Type' error={fetchProjectTypeWidgetData.isError}>
            {projectTypeLabels?.length > 0 ? (
              <div className='flex flex-col p-5 max-w-screen-lg md:max-w-screen-xs'>
                <div className='relative h-96'>
                  <div className='px-5 text-start'>
                    <h3 className='text-gray-500 font-bold'>Total Projects</h3>
                    <h1 className='m-0'>
                      <NumberAbbreviation value={totalProjectType} prefix='' />
                    </h1>
                  </div>
                  <div className='md:w-7/8 xl:w-full h-full pt-4 p-0 xl:p-5'>
                    <div className='flex h-full w-full flex-col gap-6 items-center justify-center xl:flex-row'>
                      <div className='flex flex-3 min-w-0 items-center justify-center overflow-visible'>
                        <PieChart
                          maintainAspectRatio
                          ref={handlePieChart}
                          displayLegend={false}
                          tooltipPlugin={htmlTooltipPlugin}
                          labels={projectTypeLabels}
                          datasets={transformProjectTypeChartDataSets(projectsByProjectType)}
                          chartTitle='Total Project Types'
                          legendAlign='bottom'
                          legendPosition=''
                          showSlicePercentage
                        />
                      </div>
                      <HtmlLegend chartInstance={pieChart} className='flex-2 w-full' />
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className='flex justify-center items-center h-2/3 p-5'>
                <h2>There are no projects by development type.</h2>
              </div>
            )}
          </Card>
        </WidgetLayout>
      )}
    </>
  );
}
